import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Box, Button, Tab, Tabs, Typography } from "@mui/material";
import { LocalShippingOutlined, LocationOnOutlined } from "@mui/icons-material";
import DTextInput from "../../../components/Dashboard/DashboardInputs/DTextInput";
import DSelectInput from "../../../components/Dashboard/DashboardInputs/DSelectInput";
import DPhoneNumberInput from "../../../components/Dashboard/DashboardInputs/DPhoneNumberInput";
import { masterDataRepository, shouldMakeAPICall } from "../../../offline";
import { API_ENDPOINT } from "../../../services/api/resources";
import classes from "./RFQTab.module.css";

const emptyAddress = {
  shipping_address1: "", shipping_address2: "", country: "", state: "",
  city: "", pincode: "", mobile_number: "",
};
const requiredFields = ["shipping_address1", "country", "state", "city", "pincode"];

export default function QuickRFQShippingAddress({ addresses, selectedAddress, onSelect, organizationName, authToken, buyerToken }) {
  const [tab, setTab] = useState("recent");
  const [recentSelection, setRecentSelection] = useState(selectedAddress);
  const [draft, setDraft] = useState(emptyAddress);
  const [phoneValid, setPhoneValid] = useState(false);
  const [touched, setTouched] = useState({});
  const [locations, setLocations] = useState({ countries: [], states: [], cities: [] });
  const [loadLocations, setLoadLocations] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locationError, setLocationError] = useState("");

  const newAddress = useMemo(() => {
    if (requiredFields.some((key) => !draft[key].trim()) || !phoneValid) return null;
    return Object.fromEntries(Object.entries(draft).map(([key, value]) => [key, value.trim()]));
  }, [draft, phoneValid]);

  useEffect(() => {
    if (tab === "new" && selectedAddress !== newAddress) onSelect(newAddress);
  }, [tab, newAddress, selectedAddress, onSelect]);

  useEffect(() => {
    if (!loadLocations) return;
    let active = true;
    const load = async () => {
      setLoading(true);
      setLocationError("");
      const results = await Promise.allSettled([
        (async () => {
          const cached = await masterDataRepository.getCountriesFromCache();
          if (cached?.length || !shouldMakeAPICall(authToken, buyerToken)) return cached;
          const response = await axios.post(API_ENDPOINT.view_countries, {}, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          const countries = response.data?.response || response.data || [];
          if (Array.isArray(countries)) await masterDataRepository.saveCountriesToCache(countries);
          return countries;
        })(),
        (async () => {
          const cached = await masterDataRepository.getStatesFromCache(null);
          return cached?.length ? cached : masterDataRepository.syncStates(authToken, null);
        })(),
        (async () => {
          const cached = await masterDataRepository.getCitiesFromCache(null);
          return cached?.length ? cached : masterDataRepository.syncCities(authToken, null);
        })(),
      ]);
      if (!active) return;
      const lists = results.map((result) => result.status === "fulfilled" && Array.isArray(result.value)
        ? result.value.map((item) => item.data || item) : []);
      setLocations({ countries: lists[0], states: lists[1], cities: lists[2] });
      if (lists.some((list) => !list.length)) setLocationError("Some locations could not be loaded. You can enter them manually.");
      setLoading(false);
    };
    load();
    return () => { active = false; };
  }, [loadLocations, authToken, buyerToken]);

  const country = locations.countries.find((item) => item.name === draft.country);
  const states = country ? locations.states.filter((item) =>
    (country.id != null && String(item.country_id) === String(country.id)) ||
    (country.token && item.country_token === country.token)) : [];
  const state = states.find((item) => item.name === draft.state);
  const cities = state ? locations.cities.filter((item) =>
    (state.id != null && String(item.state_id) === String(state.id)) ||
    (state.token && item.state_token === state.token)) : [];
  const update = (key, value) => {
    setTouched((previous) => ({ ...previous, [key]: true }));
    setDraft((previous) => ({
      ...previous, [key]: value,
      ...(key === "country" ? { state: "", city: "" } : key === "state" ? { city: "" } : {}),
    }));
  };
  const changeTab = (value) => {
    setTab(value);
    if (value === "new") setLoadLocations(true);
    onSelect(value === "recent" ? recentSelection : newAddress);
  };
  const textField = (key, label, required = true, disabled = false) => (
    <DTextInput label={label} required={required} size="100%" disabled={disabled}
      value={draft[key]} onChange={(event) => update(key, event.target.value)}
      error={Boolean(touched[key] && required && !draft[key].trim())} errorMsg={`${label} is required`} />
  );
  const locationField = (key, label, list, disabled) => list.length ? (
    <DSelectInput label={label} required defaultDropdown noInit disabled={disabled}
      value={draft[key]} options={[...new Set(list.map((item) => item.name))]}
      onChange={(value) => update(key, value || "")}
      error={Boolean(touched[key] && !draft[key].trim())} errorMsg={`${label} is required`} />
  ) : textField(key, label, true, disabled);
  const recentAddresses = Array.isArray(addresses) ? addresses : [];

  return (
    <Box sx={{ "& .MuiTab-root, & .MuiButton-root": { fontSize: "14px", textTransform: "none" } }}>
      <Tabs value={tab} onChange={(_, value) => changeTab(value)} aria-label="Shipping address options" sx={{ mb: 2 }}>
        <Tab value="recent" label="Recent Addresses" id="shipping-recent-tab" aria-controls="shipping-recent-panel" />
        <Tab value="new" label="Add New Address" id="shipping-new-tab" aria-controls="shipping-new-panel" />
      </Tabs>
      <div role="tabpanel" id="shipping-recent-panel" aria-labelledby="shipping-recent-tab" hidden={tab !== "recent"}>
        {recentAddresses.length ? (
          <div className={classes.cardcontainer}>
            {recentAddresses.map((address, index) => (
              <label key={index} className={`${classes.card} ${selectedAddress === address ? classes.selectedCard : ""}`}>
                <input type="radio" name="shippingAddress" checked={selectedAddress === address}
                  onChange={() => { setRecentSelection(address); onSelect(address); }} className={classes.radioButton} />
                <h3>{organizationName}, {address.city}</h3>
                <p><strong>Address:</strong> {address.shipping_address1}, {address.shipping_address2 || ""}</p>
                <p><strong>City:</strong> {address.city}</p>
                <p><strong>State:</strong> {address.state}</p>
                <p><strong>Country:</strong> {address.country}</p>
                <p><strong>Pincode:</strong> {address.pincode}</p>
                <p><strong>Mobile Number:</strong> {address.mobile_number}</p>
              </label>
            ))}
          </div>
        ) : (
          <Box sx={{ p: 3, border: "1px solid #e2e8f0", borderRadius: 2 }}>
            <Typography sx={{ fontSize: "14px", mb: 1 }}>No recent shipping addresses. Add a delivery address to continue.</Typography>
            <Button onClick={() => changeTab("new")}>Add New Address</Button>
          </Box>
        )}
      </div>
      <div role="tabpanel" id="shipping-new-panel" aria-labelledby="shipping-new-tab" hidden={tab !== "new"}>
        <div className={classes.newRfqShippingLayout}>
          <div className={classes.newRfqShippingVisual}>
            <div className={classes.newRfqShippingMap}>
              <span className={classes.newRfqShippingPin}><LocationOnOutlined /></span>
              <span className={classes.newRfqShippingTruck}><LocalShippingOutlined /></span>
            </div>
            <div className={classes.newRfqShippingCopy}>
              <span><LocationOnOutlined /></span>
              <div><h3>Shipping Address</h3><p>Delivery details for supplier coordination.</p></div>
            </div>
            <div className={classes.newRfqShippingNote}>Ensure delivery location is accurate for smooth and timely fulfillment.</div>
          </div>
          <div className={classes.input_fields_wrapper}>
            {textField("shipping_address1", "Address Line 1")}
            {textField("shipping_address2", "Address Line 2 (Optional)", false)}
            {locationField("country", "Country", locations.countries, loading)}
            {locationField("state", "State/Province/Region", states, loading || !draft.country)}
            {locationField("city", "City", cities, loading || !draft.state)}
            {textField("pincode", "Postal/Zipcode")}
            <DPhoneNumberInput label="Mobile Number" required maxDigits={10} value={draft.mobile_number}
              onChange={(value, data, valid, formattedValue) => {
                update("mobile_number", value ? formattedValue || value : "");
                setPhoneValid(Boolean(value && valid));
              }} error={Boolean(touched.mobile_number && !phoneValid)} errorMsg="Enter a valid mobile number" />
          </div>
        </div>
        {locationError && <Typography role="status" sx={{ fontSize: "14px", mt: 1 }}>{locationError}</Typography>}
      </div>
    </Box>
  );
}
